
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { COLORS } from '../constants';
import { useAuth } from '../contexts/AuthContext';

// --- Types ---
type ParetoItem = { name: string; count: number; cumulative: number };
type PoQualityItem = { po: string; total: number; passed: number; failed: number; passRate: number; date: string };
type SkuQualityItem = { sku: string; total: number; passed: number; failed: number; passRate: number };
type DefectCategoryItem = { name: string; count: number };
type DefectSeverityItem = { name: string; value: number; color: string };
type VendorTrendItem = { name: string; passRate: number; defects: number };
type VendorHistoryItem = { date: string; defects: number; avg: number; critical: number; major: number; minor: number };
type VendorDefectItem = { id: string; date: string; type: string; severity: string; po: string; sku: string; remarks: string };
type VendorDetail = {
  stats: { total: number; dailyAvg: number; criticalCount: number };
  history: VendorHistoryItem[];
  defectList: VendorDefectItem[];
};
type TrendItem = { period: string; passed: number; failed: number; passRate: number; defects: number };
type AqlOffender = {
  po: string; level: string; lotSize: number;
  critical: { allowed: number; actual: number };
  major: { allowed: number; actual: number };
  minor: { allowed: number; actual: number };
  overBy: number;
};
type AqlMarginData = { compliance: { within: number; exceeded: number }; worstOffenders: AqlOffender[] };
type FilterOptions = {
  pos: string[];
  skus: string[];
  inspectors: { id: string; name: string }[];
  facilities: { id: string; name: string; code: string }[];
  buyers: string[];
  inspectionTypes: { value: string; label: string }[];
};

// --- Helpers ---
const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3000';

const buildQuery = (filters: Record<string, string>) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v && v.trim() !== '') params.append(k, v);
  });
  const s = params.toString();
  return s ? `?${s}` : '';
};

const SkeletonChart: React.FC<{ height?: string }> = ({ height = 'h-[400px]' }) => (
  <div className={`${height} w-full flex items-center justify-center`}>
    <div className="flex flex-col items-center gap-3 text-slate-300">
      <span className="material-symbols-outlined text-5xl animate-pulse">analytics</span>
      <span className="text-xs font-bold uppercase tracking-wider">Loading data...</span>
    </div>
  </div>
);

const EmptyState: React.FC<{ label?: string; height?: string }> = ({ label = 'No data for selected filters', height = 'h-[400px]' }) => (
  <div className={`${height} w-full flex items-center justify-center`}>
    <div className="flex flex-col items-center gap-3 text-slate-300">
      <span className="material-symbols-outlined text-5xl">inbox</span>
      <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
    </div>
  </div>
);

const Analytics: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<'quality' | 'defects' | 'vendors' | 'pareto' | 'trends'>('pareto');
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    po: '',
    sku: '',
    inspectionType: '',
    inspector: '',
    facility: '',
    buyer: '',
    startDate: '',
    endDate: ''
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Debounce filter changes (300ms) before triggering refetch
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(t);
  }, [filters]);

  const qs = useMemo(() => buildQuery(debouncedFilters), [debouncedFilters]);

  // --- API data ---
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [paretoData, setParetoData] = useState<ParetoItem[]>([]);
  const [poQualityData, setPoQualityData] = useState<PoQualityItem[]>([]);
  const [skuQualityData, setSkuQualityData] = useState<SkuQualityItem[]>([]);
  const [defectCategories, setDefectCategories] = useState<DefectCategoryItem[]>([]);
  const [defectSeverity, setDefectSeverity] = useState<DefectSeverityItem[]>([]);
  const [vendorTrends, setVendorTrends] = useState<VendorTrendItem[]>([]);
  const [vendorDetail, setVendorDetail] = useState<VendorDetail | null>(null);
  const [trendsData, setTrendsData] = useState<TrendItem[]>([]);
  const [aqlMarginData, setAqlMarginData] = useState<AqlMarginData | null>(null);

  const [loading, setLoading] = useState({
    pareto: false, quality: false, defects: false, vendors: false, vendorDetail: false, trends: false, aql: false,
  });

  // Auth-aware fetch helper
  const apiFetch = useCallback(async (path: string) => {
    const res = await fetch(`${baseUrl}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Request failed');
    return json.data;
  }, [token]);

  // --- Load filter options once ---
  useEffect(() => {
    if (!token) return;
    apiFetch('/api/v1/analytics/filter-options')
      .then((data) => setFilterOptions(data))
      .catch((err) => console.error('filter-options fetch failed:', err));
  }, [token, apiFetch]);

  // --- Per-tab data fetching ---
  useEffect(() => {
    if (!token || activeTab !== 'pareto') return;
    setLoading((l) => ({ ...l, pareto: true }));
    apiFetch(`/api/v1/analytics/pareto${qs}`)
      .then((data) => setParetoData(data || []))
      .catch((err) => { console.error('pareto fetch failed:', err); setParetoData([]); })
      .finally(() => setLoading((l) => ({ ...l, pareto: false })));
  }, [activeTab, qs, token, apiFetch]);

  useEffect(() => {
    if (!token || activeTab !== 'quality') return;
    setLoading((l) => ({ ...l, quality: true }));
    apiFetch(`/api/v1/analytics/quality${qs}`)
      .then((data) => {
        setPoQualityData(data?.poData || []);
        setSkuQualityData(data?.skuData || []);
      })
      .catch((err) => { console.error('quality fetch failed:', err); setPoQualityData([]); setSkuQualityData([]); })
      .finally(() => setLoading((l) => ({ ...l, quality: false })));
  }, [activeTab, qs, token, apiFetch]);

  useEffect(() => {
    if (!token || activeTab !== 'defects') return;
    setLoading((l) => ({ ...l, defects: true }));
    apiFetch(`/api/v1/analytics/defects${qs}`)
      .then((data) => {
        setDefectCategories(data?.categories || []);
        setDefectSeverity(data?.severity || []);
      })
      .catch((err) => { console.error('defects fetch failed:', err); setDefectCategories([]); setDefectSeverity([]); })
      .finally(() => setLoading((l) => ({ ...l, defects: false })));
  }, [activeTab, qs, token, apiFetch]);

  useEffect(() => {
    if (!token || activeTab !== 'vendors' || selectedVendor) return;
    setLoading((l) => ({ ...l, vendors: true }));
    apiFetch(`/api/v1/analytics/vendors${qs}`)
      .then((data) => setVendorTrends(data || []))
      .catch((err) => { console.error('vendors fetch failed:', err); setVendorTrends([]); })
      .finally(() => setLoading((l) => ({ ...l, vendors: false })));
  }, [activeTab, qs, token, apiFetch, selectedVendor]);

  useEffect(() => {
    if (!token || activeTab !== 'vendors' || !selectedVendor) return;
    setLoading((l) => ({ ...l, vendorDetail: true }));
    const sep = qs ? '&' : '?';
    apiFetch(`/api/v1/analytics/vendors${qs}${sep}vendor=${encodeURIComponent(selectedVendor)}`)
      .then((data) => setVendorDetail(data || null))
      .catch((err) => { console.error('vendor detail fetch failed:', err); setVendorDetail(null); })
      .finally(() => setLoading((l) => ({ ...l, vendorDetail: false })));
  }, [activeTab, qs, token, apiFetch, selectedVendor]);

  useEffect(() => {
    if (!token || activeTab !== 'trends') return;
    setLoading((l) => ({ ...l, trends: true, aql: true }));
    apiFetch(`/api/v1/analytics/trends${qs}`)
      .then((data) => setTrendsData(data || []))
      .catch((err) => { console.error('trends fetch failed:', err); setTrendsData([]); })
      .finally(() => setLoading((l) => ({ ...l, trends: false })));
    apiFetch(`/api/v1/analytics/aql-margin${qs}`)
      .then((data) => setAqlMarginData(data || null))
      .catch((err) => { console.error('aql-margin fetch failed:', err); setAqlMarginData(null); })
      .finally(() => setLoading((l) => ({ ...l, aql: false })));
  }, [activeTab, qs, token, apiFetch]);

  const tabs = [
    { id: 'pareto', label: 'Pareto Analysis', icon: 'analytics' },
    { id: 'quality', label: 'Pass - Fail %', icon: 'check_circle' },
    { id: 'defects', label: 'Defect Trends', icon: 'inventory_2' },
    { id: 'vendors', label: 'Vendor Trends', icon: 'factory' },
    { id: 'trends', label: 'Trends & AQL', icon: 'trending_up' },
  ];

  const clearFilters = () => {
    setFilters({
      po: '', sku: '', inspectionType: '', inspector: '',
      facility: '', buyer: '', startDate: '', endDate: ''
    });
  };

  return (
    <div className="mx-auto w-full animate-in fade-in duration-500">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Advanced Analytics</h1>
          <p className="text-sm text-slate-500 font-medium">Visualizing quality intelligence and performance metrics.</p>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-[#136bce] shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'pareto' && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <div className="mb-6 flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-[#136bce]">signal_cellular_alt</span>
                Pareto Analysis (Defects)
              </h3>
              <p className="text-xs text-slate-500 font-medium max-w-2xl leading-relaxed">
                Based on the 80/20 rule, the Pareto chart identifies the majority of defects (80%)
                are caused by a small number of defect types (20%). Focus corrective actions on
                the leftmost categories for maximum impact.
              </p>
            </div>
            <button className="shrink-0 px-4 py-2 bg-[#136bce] text-white rounded-xl text-xs font-bold hover:bg-[#115fb8] transition-all flex items-center gap-2 shadow-sm">
              <span className="material-symbols-outlined text-sm">download</span>
              DOWNLOAD REPORT
            </button>
          </div>
          <div className="h-[450px] w-full">
            {loading.pareto ? (
              <SkeletonChart height="h-[450px]" />
            ) : paretoData.length === 0 ? (
              <EmptyState height="h-[450px]" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={paretoData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} label={{ value: 'Frequency', angle: -90, position: 'insideLeft', offset: 10 }} />
                  <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} label={{ value: 'Cumulative %', angle: 90, position: 'insideRight', offset: 10 }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="count" name="Frequency" fill={COLORS.blue} radius={[4, 4, 0, 0]} barSize={40} />
                  <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Cumulative %" stroke={COLORS.red} strokeWidth={3} dot={{ r: 5, fill: COLORS.red }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Global Filter Section (Shared by Quality, Defects, Vendors, and Trends) */}
      {(activeTab === 'quality' || activeTab === 'defects' || activeTab === 'vendors' || activeTab === 'trends') && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/60 mb-8 animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <span className="material-symbols-outlined text-[#136bce]">filter_list</span>
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Intelligence Filters</h3>
                <p className="text-[10px] text-slate-400 font-medium">Refine your data analysis by multiple dimensions</p>
              </div>
            </div>
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-[#136bce] bg-blue-50 hover:bg-blue-100 transition-colors flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-sm">refresh</span>
              RESET FILTERS
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-5">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">
                <span className="material-symbols-outlined text-xs">local_shipping</span>
                Purchase Order
              </label>
              <div className="relative group">
                <input
                  type="text"
                  list="po-options"
                  placeholder="e.g. PO-8822"
                  className="w-full text-xs bg-slate-50/50 border-slate-200 rounded-xl py-2.5 px-3 transition-all focus:bg-white focus:ring-2 focus:ring-[#136bce]/10 focus:border-[#136bce] placeholder:text-slate-300"
                  value={filters.po}
                  onChange={(e) => setFilters({...filters, po: e.target.value})}
                />
                <datalist id="po-options">
                  {filterOptions?.pos.map((p) => <option key={p} value={p} />)}
                </datalist>
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">
                <span className="material-symbols-outlined text-xs">style</span>
                SKU / Style
              </label>
              <div className="relative group">
                <input
                  type="text"
                  list="sku-options"
                  placeholder="Search SKU..."
                  className="w-full text-xs bg-slate-50/50 border-slate-200 rounded-xl py-2.5 px-3 transition-all focus:bg-white focus:ring-2 focus:ring-[#136bce]/10 focus:border-[#136bce] placeholder:text-slate-300"
                  value={filters.sku}
                  onChange={(e) => setFilters({...filters, sku: e.target.value})}
                />
                <datalist id="sku-options">
                  {filterOptions?.skus.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">
                <span className="material-symbols-outlined text-xs">manage_search</span>
                Inspection Type
              </label>
              <select
                className="w-full text-xs bg-slate-50/50 border-slate-200 rounded-xl py-2.5 px-3 transition-all focus:bg-white focus:ring-2 focus:ring-[#136bce]/10 focus:border-[#136bce] appearance-none"
                value={filters.inspectionType}
                onChange={(e) => setFilters({...filters, inspectionType: e.target.value})}
              >
                <option value="">All Verification Types</option>
                {(filterOptions?.inspectionTypes || []).map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">
                <span className="material-symbols-outlined text-xs">person</span>
                Inspector
              </label>
              <select
                className="w-full text-xs bg-slate-50/50 border-slate-200 rounded-xl py-2.5 px-3 transition-all focus:bg-white focus:ring-2 focus:ring-[#136bce]/10 focus:border-[#136bce] appearance-none"
                value={filters.inspector}
                onChange={(e) => setFilters({...filters, inspector: e.target.value})}
              >
                <option value="">All Inspectors</option>
                {(filterOptions?.inspectors || []).map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">
                <span className="material-symbols-outlined text-xs">domain</span>
                Production Facility
              </label>
              <select
                className="w-full text-xs bg-slate-50/50 border-slate-200 rounded-xl py-2.5 px-3 transition-all focus:bg-white focus:ring-2 focus:ring-[#136bce]/10 focus:border-[#136bce]"
                value={filters.facility}
                onChange={(e) => setFilters({...filters, facility: e.target.value})}
              >
                <option value="">All Global Facilities</option>
                {(filterOptions?.facilities || []).map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">
                <span className="material-symbols-outlined text-xs">shopping_bag</span>
                Target Buyer
              </label>
              <input
                type="text"
                list="buyer-options"
                placeholder="Search Buyer..."
                className="w-full text-xs bg-slate-50/50 border-slate-200 rounded-xl py-2.5 px-3 transition-all focus:bg-white focus:ring-2 focus:ring-[#136bce]/10 focus:border-[#136bce] placeholder:text-slate-300"
                value={filters.buyer}
                onChange={(e) => setFilters({...filters, buyer: e.target.value})}
              />
              <datalist id="buyer-options">
                {filterOptions?.buyers.map((b) => <option key={b} value={b} />)}
              </datalist>
            </div>

            <div className="space-y-2 xl:col-span-2">
              <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">
                <span className="material-symbols-outlined text-xs">calendar_today</span>
                Time Range Performance
              </label>
              {!filters.startDate && !filters.endDate && (
                <p className="text-[10px] text-slate-400 font-medium ml-1">
                  No range selected — showing the last 365 days by default.
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">FROM</span>
                  <input
                    type="date"
                    className="w-full text-[10px] bg-slate-50/50 border-slate-200 rounded-xl py-2.5 pl-12 pr-3 transition-all focus:bg-white focus:ring-2 focus:ring-[#136bce]/10 focus:border-[#136bce]"
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  />
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">TO</span>
                  <input
                    type="date"
                    className="w-full text-[10px] bg-slate-50/50 border-slate-200 rounded-xl py-2.5 pl-8 pr-3 transition-all focus:bg-white focus:ring-2 focus:ring-[#136bce]/10 focus:border-[#136bce]"
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'quality' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                <span className="material-symbols-outlined text-sm">check_circle</span>
              </div>
              <h3 className="font-bold text-lg text-slate-800">Pass - Fail Analysis</h3>
            </div>
            <button className="px-4 py-2 bg-[#136bce] text-white rounded-xl text-xs font-bold hover:bg-[#115fb8] transition-all flex items-center gap-2 shadow-sm">
              <span className="material-symbols-outlined text-sm">download</span>
              DOWNLOAD REPORT
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-800 mb-8 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">fact_check</span>
                PO Pass Rate Distribution
              </h3>
              <div className="h-[400px] w-full">
                {loading.quality ? (
                  <SkeletonChart />
                ) : poQualityData.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={poQualityData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="po" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                      <Legend />
                      <Bar dataKey="passRate" name="Pass Rate %" fill={COLORS.green} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-800 mb-8 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">equalizer</span>
                Pass vs Fail Counts
              </h3>
              <div className="h-[400px] w-full">
                {loading.quality ? (
                  <SkeletonChart />
                ) : poQualityData.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={poQualityData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="po" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                      <Legend />
                      <Bar dataKey="passed" name="Passed" stackId="a" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="failed" name="Failed" stackId="a" fill={COLORS.red} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">list_alt</span>
                Detailed Quality Metrics by PO
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-4 border-r border-slate-100/50">Purchase Order</th>
                    <th className="px-6 py-4 border-r border-slate-100/50 text-center">Date</th>
                    <th className="px-6 py-4 text-center">Total Quantity</th>
                    <th className="px-6 py-4 text-center text-emerald-600">Passed</th>
                    <th className="px-6 py-4 text-center text-red-600">Failed</th>
                    <th className="px-6 py-4 text-center">Pass Rate</th>
                    <th className="px-6 py-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {poQualityData.length === 0 && !loading.quality ? (
                    <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-400 text-sm font-medium">No quality data available for the selected filters.</td></tr>
                  ) : (
                    poQualityData.map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-bold text-slate-900 border-r border-slate-100/50">{item.po}</td>
                        <td className="px-6 py-4 text-slate-500 border-r border-slate-100/50 font-medium text-center whitespace-nowrap">{item.date || '—'}</td>
                        <td className="px-6 py-4 font-medium text-center">{item.total.toLocaleString()}</td>
                        <td className="px-6 py-4 text-emerald-600 font-bold text-center">{item.passed.toLocaleString()}</td>
                        <td className="px-6 py-4 text-red-600 font-bold text-center">{item.failed.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${item.passRate}%`,
                                  backgroundColor: item.passRate > 90 ? COLORS.green : item.passRate > 80 ? COLORS.yellow : COLORS.red
                                }}
                              ></div>
                            </div>
                            <span className="font-bold min-w-[35px] text-right">{item.passRate}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase inline-block min-w-[70px] ${
                            item.passRate > 90 ? 'bg-emerald-100 text-emerald-700' :
                            item.passRate > 80 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {item.passRate > 90 ? 'Premium' : item.passRate > 80 ? 'Standard' : 'Warning'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* SKU Level Section */}
          <div className="space-y-6 pt-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-[#136bce]">
                <span className="material-symbols-outlined text-sm">inventory</span>
              </div>
              <h3 className="font-bold text-lg text-slate-800">SKU Quality Performance</h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-8">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Top SKUs by Pass Rate</h4>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS.blue }}></div>
                      <span className="text-[10px] font-bold text-slate-400">PASSED</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS.red }}></div>
                      <span className="text-[10px] font-bold text-slate-400">FAILED</span>
                    </div>
                  </div>
                </div>
                <div className="h-[350px] w-full">
                  {loading.quality ? (
                    <SkeletonChart height="h-[350px]" />
                  ) : skuQualityData.length === 0 ? (
                    <EmptyState height="h-[350px]" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={skuQualityData} layout="vertical">
                        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <YAxis dataKey="sku" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} width={100} />
                        <Tooltip
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                          cursor={{ fill: '#f1f5f9', opacity: 0.4 }}
                        />
                        <Bar dataKey="passed" stackId="a" fill={COLORS.blue} barSize={24} radius={[0, 0, 0, 0]} />
                        <Bar dataKey="failed" stackId="a" fill={COLORS.red} barSize={24} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Quality Insight panel — kept as static template (no AI/heuristic in scope) */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 flex flex-col">
                <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-6">Quality Insight</h4>
                <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center relative">
                    <span className="material-symbols-outlined text-[#136bce] text-4xl">analytics</span>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white">
                      <span className="material-symbols-outlined text-white text-[10px] font-bold">trending_up</span>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-bold text-slate-900">SKU Performance Overview</h5>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                      Showing top {skuQualityData.length} SKUs ranked by inspection volume.
                      Use the filters above to narrow analysis by PO, buyer, or inspector.
                    </p>
                  </div>
                  {skuQualityData.length > 0 && (
                    <div className="w-full pt-4 space-y-3">
                      {(() => {
                        const worst = [...skuQualityData].sort((a, b) => a.passRate - b.passRate)[0];
                        if (!worst || worst.passRate >= 90) return null;
                        return (
                          <div className="p-3 bg-red-50 rounded-lg border border-red-100 text-left">
                            <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1">Lowest Pass Rate</p>
                            <p className="text-[11px] text-red-800 font-medium">{worst.sku} is at {worst.passRate}% — investigate quality issues.</p>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#136bce] text-white font-bold uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-4">SKU / Style Reference</th>
                      <th className="px-6 py-4 text-center">Produced Units</th>
                      <th className="px-6 py-4 text-center">Quality Units</th>
                      <th className="px-6 py-4 text-center text-red-100">Defect Units</th>
                      <th className="px-6 py-4 text-center">Yield %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {skuQualityData.length === 0 && !loading.quality ? (
                      <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400 text-sm font-medium">No SKU data available.</td></tr>
                    ) : (
                      skuQualityData.map((sku, idx) => (
                        <tr key={idx} className="hover:bg-blue-50/30 transition-colors">
                          <td className="px-6 py-4 font-bold text-[#136bce]">{sku.sku}</td>
                          <td className="px-6 py-4 text-center font-medium text-slate-600">{sku.total.toLocaleString()}</td>
                          <td className="px-6 py-4 text-center text-emerald-600 font-bold">{sku.passed.toLocaleString()}</td>
                          <td className="px-6 py-4 text-center text-red-500 font-bold">{sku.failed.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <span className={`font-bold px-2 py-0.5 rounded text-[10px] ${
                                sku.passRate > 95 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {sku.passRate}%
                              </span>
                              <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${sku.passRate > 95 ? 'bg-emerald-500' : 'bg-red-500'}`}
                                  style={{ width: `${sku.passRate}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'defects' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-[#136bce]">
                <span className="material-symbols-outlined text-sm">inventory_2</span>
              </div>
              <h3 className="font-bold text-lg text-slate-800">Defect Analytics</h3>
            </div>
            <button className="px-4 py-2 bg-[#136bce] text-white rounded-xl text-xs font-bold hover:bg-[#115fb8] transition-all flex items-center gap-2 shadow-sm">
              <span className="material-symbols-outlined text-sm">download</span>
              DOWNLOAD REPORT
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-800 mb-8 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">bar_chart</span>
                Defect Distribution by Type
              </h3>
              <div className="h-[400px] w-full">
                {loading.defects ? (
                  <SkeletonChart />
                ) : defectCategories.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={defectCategories} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b', fontWeight: 500 }} width={120} />
                      <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                      <Bar dataKey="count" fill={COLORS.blue} radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-800 mb-8 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">pie_chart</span>
                Severity Breakdown
              </h3>
              <div className="h-[400px] w-full flex items-center">
                {loading.defects ? (
                  <SkeletonChart />
                ) : defectSeverity.every(s => s.value === 0) ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={defectSeverity}
                        innerRadius={80}
                        outerRadius={120}
                        paddingAngle={8}
                        dataKey="value"
                      >
                        {defectSeverity.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                      <Legend verticalAlign="bottom" height={36}/>
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'vendors' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-[#f59e0b]">
                <span className="material-symbols-outlined text-sm">factory</span>
              </div>
              <h3 className="font-bold text-lg text-slate-800">Vendor Performance Trends</h3>
            </div>
            <button className="px-4 py-2 bg-[#136bce] text-white rounded-xl text-xs font-bold hover:bg-[#115fb8] transition-all flex items-center gap-2 shadow-sm">
              <span className="material-symbols-outlined text-sm">download</span>
              DOWNLOAD REPORT
            </button>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                <span className="material-symbols-outlined text-[#f59e0b]">factory</span>
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Vendor Intelligence</h3>
                <p className="text-[10px] text-slate-400 font-medium">Select a vendor for deep-dive defect analysis</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <select
                className="text-xs bg-slate-50 border-slate-200 rounded-xl py-2 px-4 focus:ring-2 focus:ring-orange-100 focus:border-[#f59e0b] min-w-[200px]"
                value={selectedVendor || ''}
                onChange={(e) => setSelectedVendor(e.target.value || null)}
              >
                <option value="">All Vendors (Global)</option>
                {vendorTrends.map(v => <option key={v.name} value={v.name}>{v.name}</option>)}
              </select>
              {selectedVendor && (
                <button
                  onClick={() => setSelectedVendor(null)}
                  className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-lg">close</span>
                </button>
              )}
            </div>
          </div>

          {!selectedVendor ? (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 animate-in fade-in duration-500">
              <h3 className="font-bold text-slate-800 mb-8 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">group</span>
                Global Vendor Benchmarking
              </h3>
              <div className="h-[450px] w-full">
                {loading.vendors ? (
                  <SkeletonChart height="h-[450px]" />
                ) : vendorTrends.length === 0 ? (
                  <EmptyState height="h-[450px]" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={vendorTrends}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                      <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar dataKey="passRate" name="Pass Rate (%)" fill={COLORS.green} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="defects" name="Total Defects" fill={COLORS.red} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                    <span className="material-symbols-outlined text-[#136bce]">bug_report</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900">{vendorDetail?.stats.total ?? 0}</h4>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total Defects</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                    <span className="material-symbols-outlined text-emerald-500">monitoring</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900">{vendorDetail?.stats.dailyAvg ?? 0}</h4>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Avg Defects / Day</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                    <span className="material-symbols-outlined text-red-500">priority_high</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900">{vendorDetail?.stats.criticalCount ?? 0}</h4>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Critical Issues</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#136bce]">bar_chart</span>
                    {selectedVendor}: Daily Defect Distribution
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Critical</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-orange-400"></div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Major</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Minor</span>
                    </div>
                  </div>
                </div>
                <div className="h-[400px] w-full">
                  {loading.vendorDetail ? (
                    <SkeletonChart />
                  ) : !vendorDetail || vendorDetail.history.length === 0 ? (
                    <EmptyState label="No defect history for this vendor in the selected range" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={vendorDetail.history}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                        <Tooltip
                          cursor={{ fill: '#f8fafc' }}
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                        />
                        <Bar dataKey="critical" stackId="a" fill={COLORS.red} radius={[0, 0, 0, 0]} />
                        <Bar dataKey="major" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="minor" stackId="a" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <span className="material-symbols-outlined text-[#136bce]">analytics</span>
                    Severity Performance Explanation
                  </h3>
                  <div className="h-[300px] w-full">
                    {loading.vendorDetail || !vendorDetail || vendorDetail.stats.total === 0 ? (
                      <EmptyState height="h-[300px]" />
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Critical', value: vendorDetail.stats.criticalCount, color: COLORS.red },
                              { name: 'Major/Minor', value: vendorDetail.stats.total - vendorDetail.stats.criticalCount, color: COLORS.blue }
                            ]}
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            <Cell fill={COLORS.red} />
                            <Cell fill={COLORS.blue} />
                          </Pie>
                          <Tooltip />
                          <Legend verticalAlign="bottom" height={36}/>
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-4 leading-relaxed bg-slate-50 p-3 rounded-lg border border-slate-100">
                    <strong>Trend Note:</strong> The chart highlights the ratio of critical vs non-critical defects.
                    A higher concentration of critical defects indicates systemic production issues that require
                    immediate CAPA (Corrective and Preventive Action) from the supplier.
                  </p>
                </div>

                {/* Supplier Rating card — static template (no rating field in DB) */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 flex flex-col justify-center">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto">
                      <span className="material-symbols-outlined text-[#136bce] text-3xl">verified_user</span>
                    </div>
                    <h3 className="font-bold text-slate-900">Supplier Quality Rating</h3>
                    <div className="flex justify-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <span key={s} className="material-symbols-outlined text-amber-400">
                          {s <= 4 ? 'star' : 'star_half'}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm font-bold text-slate-500">Tier 2 Supplier - High Priority Review</p>
                    <div className="pt-4">
                      <button className="bg-[#136bce] text-white text-[10px] font-bold px-6 py-2.5 rounded-xl hover:shadow-lg hover:shadow-blue-200 transition-all uppercase tracking-widest">
                        GENERATE PERFORMANCE REPORT
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#136bce]">event_note</span>
                    Incident Log for Supplier Review
                  </h3>
                  <button className="text-[10px] font-bold text-slate-400 flex items-center gap-1 hover:text-slate-600 transition-colors">
                    <span className="material-symbols-outlined text-sm">download</span>
                    DOWNLOAD LOG
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-4">Date</th>
                        <th className="px-6 py-4">PO Reference</th>
                        <th className="px-6 py-4">SKU Ref</th>
                        <th className="px-6 py-4">Defect Type</th>
                        <th className="px-6 py-4">Severity</th>
                        <th className="px-6 py-4">Remarks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {!vendorDetail || vendorDetail.defectList.length === 0 ? (
                        <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400 text-sm font-medium">
                          {loading.vendorDetail ? 'Loading incidents...' : 'No incidents recorded for this vendor.'}
                        </td></tr>
                      ) : (
                        vendorDetail.defectList.map((defect) => (
                          <tr key={defect.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 text-slate-900 font-medium">{defect.date}</td>
                            <td className="px-6 py-4 font-bold text-[#136bce]">{defect.po}</td>
                            <td className="px-6 py-4 font-mono text-slate-500">{defect.sku}</td>
                            <td className="px-6 py-4">{defect.type}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase ${
                                defect.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                                defect.severity === 'Major' ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {defect.severity}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-slate-500 max-w-[200px] truncate" title={defect.remarks}>
                              {defect.remarks}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'trends' && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
              <span className="material-symbols-outlined text-sm">trending_up</span>
            </div>
            <h3 className="font-bold text-lg text-slate-800">Trends & AQL Compliance</h3>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce]">show_chart</span>
              Org-Wide Pass Rate & Defect Trend
            </h3>
            <p className="text-xs text-slate-500 font-medium max-w-2xl leading-relaxed mb-6">
              Monthly pass rate (completed final inspections) and total defect count across all inspection
              types, for the selected filters.
            </p>
            <div className="h-[400px] w-full">
              {loading.trends ? (
                <SkeletonChart />
              ) : trendsData.length === 0 ? (
                <EmptyState label="No completed inspections in range" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trendsData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} label={{ value: 'Defects', angle: -90, position: 'insideLeft', offset: 10 }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} label={{ value: 'Pass Rate %', angle: 90, position: 'insideRight', offset: 10 }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="defects" name="Total Defects" fill={COLORS.blue} radius={[4, 4, 0, 0]} barSize={32} />
                    <Line yAxisId="right" type="monotone" dataKey="passRate" name="Pass Rate %" stroke={COLORS.green} strokeWidth={3} dot={{ r: 5, fill: COLORS.green }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">verified</span>
                AQL Compliance
              </h3>
              <p className="text-xs text-slate-500 font-medium leading-relaxed mb-4">
                Completed final inspections vs. their AQL accept-number threshold (lot size + inspection level).
              </p>
              <div className="h-[260px] w-full">
                {loading.aql ? (
                  <SkeletonChart height="h-[260px]" />
                ) : !aqlMarginData || (aqlMarginData.compliance.within + aqlMarginData.compliance.exceeded === 0) ? (
                  <EmptyState height="h-[260px]" label="No AQL-eligible inspections in range" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Within AQL', value: aqlMarginData.compliance.within, color: COLORS.green },
                          { name: 'Exceeded AQL', value: aqlMarginData.compliance.exceeded, color: COLORS.red },
                        ]}
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        <Cell fill={COLORS.green} />
                        <Cell fill={COLORS.red} />
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <span className="material-symbols-outlined text-red-500">report</span>
                  Worst AQL Offenders
                </h3>
                <p className="text-[10px] text-slate-400 font-medium mt-1">
                  Completed inspections that exceeded their accept-number threshold, sorted by how far over.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-4">PO</th>
                      <th className="px-6 py-4 text-center">Level</th>
                      <th className="px-6 py-4 text-center">Lot Size</th>
                      <th className="px-6 py-4 text-center">Critical (Ac / Found)</th>
                      <th className="px-6 py-4 text-center">Major (Ac / Found)</th>
                      <th className="px-6 py-4 text-center">Minor (Ac / Found)</th>
                      <th className="px-6 py-4 text-center">Over By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {!aqlMarginData || aqlMarginData.worstOffenders.length === 0 ? (
                      <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-400 text-sm font-medium">
                        {loading.aql ? 'Loading...' : 'No AQL threshold breaches in the selected range.'}
                      </td></tr>
                    ) : (
                      aqlMarginData.worstOffenders.map((o, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-bold text-[#136bce]">{o.po}</td>
                          <td className="px-6 py-4 text-center">{o.level}</td>
                          <td className="px-6 py-4 text-center">{o.lotSize.toLocaleString()}</td>
                          <td className={`px-6 py-4 text-center ${o.critical.actual > o.critical.allowed ? 'text-red-600 font-bold' : 'text-slate-500'}`}>{o.critical.allowed} / {o.critical.actual}</td>
                          <td className={`px-6 py-4 text-center ${o.major.actual > o.major.allowed ? 'text-red-600 font-bold' : 'text-slate-500'}`}>{o.major.allowed} / {o.major.actual}</td>
                          <td className={`px-6 py-4 text-center ${o.minor.actual > o.minor.allowed ? 'text-red-600 font-bold' : 'text-slate-500'}`}>{o.minor.allowed} / {o.minor.actual}</td>
                          <td className="px-6 py-4 text-center">
                            <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-700">+{o.overBy}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
